import { useState, useEffect } from 'react'
import { api } from '../utils/api'
import { Monitor, Eye, EyeOff } from 'lucide-react'

interface Props {
  onLogin: (user: { username: string; role: string }) => void
}

interface SsoProvider { companyId: string; companyName: string }

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true" className="flex-shrink-0">
      <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
      <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  )
}

export default function LoginPage({ onLogin }: Props) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [ssoProviders, setSsoProviders] = useState<SsoProvider[]>([])
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    api.get('/auth/saml/providers').then(r => setSsoProviders(r.data)).catch(() => {})
    const ssoError = new URLSearchParams(window.location.search).get('ssoError')
    if (ssoError) setError(ssoError)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(''); setLoading(true)
    try {
      const { data } = await api.post('/auth/login', { username, password })
      localStorage.setItem('token', data.token)
      onLogin(data.user)
    } catch {
      setError('ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง')
    } finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-blue-600 p-2.5 rounded-xl"><Monitor size={24} className="text-white"/></div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">IT Management</h1>
            <p className="text-sm text-gray-500">ระบบจัดการ IT</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อผู้ใช้</label>
            <input value={username} onChange={e => setUsername(e.target.value)} required autoFocus
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="admin"/>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">รหัสผ่าน</label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••"/>
              <button type="button" onClick={() => setShowPassword(s => !s)} tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
              </button>
            </div>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
          </button>
        </form>

        {ssoProviders.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200"/>
              <span className="text-xs text-gray-400">หรือ</span>
              <div className="flex-1 h-px bg-gray-200"/>
            </div>
            <div className="space-y-2 mt-4">
              {ssoProviders.map(p => (
                <a key={p.companyId} href={`/api/auth/saml/${p.companyId}/login`}
                  className="w-full flex items-center justify-center gap-2 border border-gray-300 rounded-xl py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                  <MicrosoftLogo/>
                  เข้าสู่ระบบด้วย Microsoft{ssoProviders.length > 1 ? ` — ${p.companyName}` : ''}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
